import React, { useEffect, useState } from 'react';
import { AssociateCardSummary, TeamProfile } from '../../../../../Types';
import { RELATION_TO_USER, SIDE_MENU_PATH } from '../../Types/types';
import RichCard from './RichCard';
import Row from '@amzn/meridian/row';
import Pagination from '@amzn/meridian/pagination';
import { card, dummyCard } from '../../Util/AssociateRichCardsStyles';
import Text from '@amzn/meridian/text';
import Select, { SelectOption } from '@amzn/meridian/select';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@amzn/meridian/box';
import {
  getBottomQuartileMetrics,
  getLargestBottomPercentile,
} from '../../../Table/TimeTrends/helper';

interface RichCardCollectionProps {
  teamProfile: TeamProfile;
  associateCardSummary: AssociateCardSummary[];
  filter: string;
  hash: string;
  openLinkHash: string;
  currentPage: number;
  currentVertical: string;
  setCurrentPage: React.Dispatch<any>;
  setHash: React.Dispatch<any>;
  setOpenLinkHash: React.Dispatch<any>;
  setExpanded: React.Dispatch<any>;
  setFocusProfile: React.Dispatch<any>;
}

const RichCardCollection: React.FC<RichCardCollectionProps> = ({
  teamProfile,
  associateCardSummary,
  filter,
  hash,
  openLinkHash,
  currentPage,
  currentVertical,
  setCurrentPage,
  setHash,
  setOpenLinkHash,
  setExpanded,
  setFocusProfile,
}: RichCardCollectionProps) => {
  const [sideMenuSource, relationSource] = hash.includes(
    SIDE_MENU_PATH.DIRECT_REPORT
  )
    ? [SIDE_MENU_PATH.DIRECT_REPORT, RELATION_TO_USER.DIRECT_REPORT]
    : hash.includes(SIDE_MENU_PATH.SHARED_PROFILE)
      ? [SIDE_MENU_PATH.SHARED_PROFILE, RELATION_TO_USER.COACHEE]
      : hash.includes(SIDE_MENU_PATH.ALL_ASSOCIATES)
        ? [SIDE_MENU_PATH.ALL_ASSOCIATES, RELATION_TO_USER.ALL_ASSOCIATES]
        : [SIDE_MENU_PATH.ALL_TMS, RELATION_TO_USER.ALL_TMS];

  const intl = useIntl();

  const filteredTeamProfile =
    relationSource === RELATION_TO_USER.ALL_ASSOCIATES
      ? teamProfile.reportingTeam.filter(profile => !profile.isManager)
      : relationSource === RELATION_TO_USER.ALL_TMS
        ? teamProfile.reportingTeam.filter(profile => profile.isManager)
        : teamProfile.reportingTeam.filter(
            profile => profile.relatedUser.relationToUser === relationSource
          );

  const defaultItemsPerPage = 12;

  const [itemPerPage, setItemPerPage] = useState(defaultItemsPerPage);

  const numberOfPages = Math.ceil(filteredTeamProfile.length / itemPerPage);

  useEffect(() => {
    setCurrentPage(1);
    setItemPerPage(defaultItemsPerPage);
  }, [openLinkHash]);

  const firstVisibleIndex = (currentPage - 1) * itemPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemPerPage;

  const visibleRecordsLen = filteredTeamProfile.length;

  useEffect(() => {
    if (itemPerPage === visibleRecordsLen && currentPage !== 1)
      setCurrentPage(1);
  }, [currentPage, itemPerPage]);

  const cardCollection = filteredTeamProfile
    .slice(firstVisibleIndex, lastVisibleIndex)
    .map((profile, index) => {
      let isBottomQuartile = associateCardSummary.some(
        summary =>
          summary.userAlias === profile.relatedUser.alias &&
          getBottomQuartileMetrics(summary).length > 0
      );
      let bottomMetrics: string[] = [];
      let bottomPercentile = '';
      if (isBottomQuartile && !profile.isManager) {
        let summary = associateCardSummary.find(
          summary => summary.userAlias === profile.relatedUser.alias
        );
        if (summary) {
          bottomMetrics = getBottomQuartileMetrics(summary);
          bottomPercentile = getLargestBottomPercentile(summary);
        }
      }

      return (
        <div key={profile.relatedUser.alias} style={card} data-cy='card'>
          <RichCard
            key={`${profile.relatedUser.alias}`}
            profile={profile}
            isBottomQuartile={isBottomQuartile && !profile.isManager}
            BottomQuartileRoutingInfo={`?bottomUser=${profile.relatedUser.alias}&bottomMetrics=${bottomMetrics}&bottomPercentile=${bottomPercentile}&selectedVertical=${currentVertical}`}
            associateCardSummary={associateCardSummary}
            filter={filter}
            sideMenuSource={sideMenuSource}
            currentVertical={currentVertical}
            setOpenLinkHash={setOpenLinkHash}
            setHash={setHash}
            setExpanded={setExpanded}
            setFocusProfile={setFocusProfile}
          />
        </div>
      );
    });

  const numberOfCardsInLastRow = filteredTeamProfile.length % 3;
  const dummyCardsNeeded =
    numberOfCardsInLastRow === 0 ? 0 : 3 - numberOfCardsInLastRow;

  const combinedCard = cardCollection.concat(
    [...Array(dummyCardsNeeded)].map((_, index) => (
      <div key={`dummy-${index}`} style={dummyCard}></div>
    ))
  );

  return (
    <>
      {combinedCard}
      {visibleRecordsLen >= defaultItemsPerPage && (
        <Box width={'100%'}>
          <Row widths='fill'>
            <Row>
              <Text>
                <FormattedMessage
                  id='cs_perfportal_arc_pagination_setting'
                  defaultMessage='Results per page'
                />
              </Text>
              <Select value={itemPerPage} onChange={setItemPerPage} width={300}>
                <SelectOption
                  value={defaultItemsPerPage}
                  label={intl.formatMessage(
                    {
                      id: 'cs_perfportal_arc_pagination_items',
                      defaultMessage: '{itemCount} items',
                    },
                    { itemCount: defaultItemsPerPage }
                  )}
                />
                <SelectOption
                  value={visibleRecordsLen}
                  label={intl.formatMessage({
                    id: 'cs_perfportal_arc_pagination_all_items',
                    defaultMessage: 'All items',
                  })}
                />
              </Select>
            </Row>
            <Row alignmentHorizontal='end'>
              <Pagination
                showSkipArrows
                numberOfPages={numberOfPages}
                onChange={setCurrentPage}
                currentPage={currentPage}
              />
            </Row>
          </Row>
        </Box>
      )}
    </>
  );
};

export default RichCardCollection;
